import { localStorageUtil } from "./storage";
import { store } from "@redux/store";
import { loginExpired } from "@redux/actions/authActions";
import Semaphore from "./semaphore";
import Router from "next/router";
import jwt from "jsonwebtoken";

const throttler = new Semaphore(1);

// Global variable to hold the token refresh promise
let refreshTokenPromise = null;

// Flag to prevent multiple logout attempts
let isLoggingOut = false;

// Helper function to safely log out user
function logoutUser() {
    if (isLoggingOut) {
        return;
    }
    isLoggingOut = true;

    localStorageUtil.removeItem("access_token");
    localStorageUtil.removeItem("user");

    // Check if store is available before dispatching
    if (store && store.dispatch) {
        try {
            store.dispatch(loginExpired());
        } catch (e) {
            console.error("Error dispatching loginExpired:", e);
        }
    }

    // Always redirect to home page when session expires
    try {
        if (typeof window !== "undefined" && Router.pathname.startsWith("/mitt")) {
            Router.replace("/");
        }
    } catch (e) {
        console.error("Error redirecting after logout:", e);
    }

    // Reset flag after a delay to allow for navigation
    setTimeout(() => {
        isLoggingOut = false;
    }, 2000);
}

// Use direct fetch for normal requests
export async function fetcherNew1819(
    path,
    method = "GET",
    body,
    sendMultiPart = false,
    signal,
    isDownload = false,
    overrideToken = null,
) {
    const api_endpoint = process.env.API_NEW_1819_URL || "https://prod.1819.is/api/v1/";
    // console.log("api_endpoint", api_endpoint);

    let accessToken = overrideToken || localStorageUtil.getItem("access_token");
    const headers = {
        accept: "application/json",
        "app-key": process.env.API_NEW_1819_APP_KEY,
    };

    if (!sendMultiPart) {
        headers["Content-Type"] = "application/json";
    }

    if (accessToken) {
        console.log("Access token found, verifying");
        headers.authorization = `Bearer ${accessToken}`;
        try {
            await jwt.verify(accessToken, process.env.ACCESS_TOKEN_PUBLIC_KEY, {
                algorithms: ["RS256"],
            });
        } catch (e) {
            console.log("Token expired, refreshing");
            await handleTokenRefresh();
            accessToken = localStorageUtil.getItem("access_token");
            headers.authorization = `Bearer ${accessToken}`;
            if (!headers.authorization) {
                throw { statusCode: 401, message: "Session expired" };
            }
        }
    }

    if (body && !sendMultiPart) {
        body = JSON.stringify(body);
    }

    let options = {
        method,
        headers,
        body,
        signal,
        credentials: "include",
    };

    // Run the fetch request directly for concurrency
    const result = await fetch(api_endpoint + path, options);
    if (result.ok) {
        try {
            return isDownload ? await result.blob() : await result.json();
        } catch (e) {
            return null;
        }
    } else if (result.status === 498) {
        await handleTokenRefresh();
        // Introduce a short delay if needed
        await new Promise((resolve) => setTimeout(resolve, 100));
        const newToken = localStorageUtil.getItem("access_token");
        if (body && !sendMultiPart && typeof body === "string") {
            body = JSON.parse(body);
        }
        return await fetcherNew1819(
            path,
            method,
            body,
            sendMultiPart,
            signal,
            isDownload,
            newToken,
        );
    } else if (result.status === 401) {
        // If we're already logging out, just throw error immediately
        if (isLoggingOut) {
            throw { statusCode: 401, message: "Session expired" };
        }

        // If we already tried to refresh the token (overrideToken exists), log out to avoid infinite loop
        if (overrideToken) {
            logoutUser();
            throw { statusCode: 401, message: "Session expired" };
        }

        // Try to refresh the token
        try {
            await handleTokenRefresh();
            const newToken = localStorageUtil.getItem("access_token");
            if (!newToken) {
                // Token refresh failed, log out
                logoutUser();
                throw { statusCode: 401, message: "Session expired" };
            }

            // Retry the request with the new token
            if (body && !sendMultiPart && typeof body === "string") {
                body = JSON.parse(body);
            }
            return await fetcherNew1819(
                path,
                method,
                body,
                sendMultiPart,
                signal,
                isDownload,
                newToken,
            );
        } catch (error) {
            // Token refresh failed, log out (only if not already logging out)
            if (!isLoggingOut) {
                logoutUser();
            }
            throw { statusCode: 401, message: "Session expired" };
        }
    } else {
        // 403 (Forbidden) og önnur villusvör: skila skilaboðum frá bakenda.
        // Token sem er útrunninn er merktur með 498 (sjá að ofan), svo 403 á
        // ekki að kveikja refresh — það er raunveruleg höfnun (t.d.
        // "Einungis starfsfólk getur skráð sig inn hér.").
        try {
            const errorString = JSON.stringify(await result.json());
            throw { statusCode: result.status, message: errorString };
        } catch (e) {
            const errorString = e.message || "Unable to reach the server";
            throw { statusCode: result.status, message: errorString };
        }
    }
}

async function handleTokenRefresh() {
    // Don't try to refresh if we're already logging out
    if (isLoggingOut) {
        throw new Error("Session expired - logging out");
    }

    const api_endpoint = process.env.API_NEW_1819_URL || "https://prod.1819.is/api/v1/";
    if (refreshTokenPromise) {
        return refreshTokenPromise;
    }
    refreshTokenPromise = (async () => {
        const accessToken = localStorageUtil.getItem("access_token");
        const headers = {
            accept: "application/json",
            "content-type": "application/json",
            "app-key": process.env.API_NEW_1819_APP_KEY,
        };

        if (accessToken) {
            headers.authorization = `Bearer ${accessToken}`;
        }

        let options = {
            method: "POST",
            headers: headers,
            credentials: "include",
        };
        try {
            let tokensResponse = await throttler.callFunction(
                fetch,
                `${api_endpoint}token`,
                options,
            );
            if (tokensResponse.status === 401) {
                logoutUser();
                throw new Error("Session expired");
            }
            if (!tokensResponse.ok) {
                throw new Error("Failed to fetch new token");
            }
            let tokens = await tokensResponse.json();
            localStorageUtil.setItem("access_token", tokens.access_token);
            // Add small delay to ensure browser commits the write
            await new Promise((resolve) => setTimeout(resolve, 100));
            return tokens.access_token;
        } catch (e) {
            console.error("Error fetching new token:", e);
            throw e;
        } finally {
            refreshTokenPromise = null;
        }
    })();
    return refreshTokenPromise;
}
